import { useEffect, useMemo, useState } from "react";

import { useMessageBus } from "@jti/fe-message-bus";
import { Typography } from "@jti/ui/components/Typography";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { ProductDescription, ProductHeading, ProductQuantityError, StockStatus } from "@product-details/components";
import { ProductCarousel } from "@product-details/components/ProductCarousel";
import { ProductPrice } from "@product-details/components/ProductPrice";
import { SticksForm } from "@product-details/forms/SticksForm";
import { useProductData, useSyncProductWithCart, type UseSyncProductWithCartProductData } from "@product-details/hooks";
import { useSticksDataContext } from "@product-details/providers";
import { dispatchViewItemEvent } from "@product-details/utils/dispatchViewItemEvent";
import { getFilteredImagesByRole } from "@product-details/utils/getFilteredImagesByRole";

import "../../../../ProductDetails.scss";

export const SticksProductDetails = () => {
  const { data, currentProduct: currentProductData } = useSticksDataContext();
  const { __ } = useTranslations();
  const {
    sku,
    badgeText = "",
    overwriteBadgeText = "",
    hasBadge = false,
    hasDiscountBadge,
    hasStrikethroughPrice,
    hidePrice,
    isCommerceDisabled,
    isSubscriptionsEnabled,
  } = data;
  const messageBus = useMessageBus();

  const [isSubscriptionSelected, setIsSubscriptionSelected] = useState(
    currentProductData.current.orderFrequency === "subscription"
  );

  const { sticksName, sticksDescription, productGalleryItems, flavor, categories, color } = useMemo(() => {
    const { name, description, flavor, categories, color } = currentProductData.current.product || {};
    const computedSticksName = name || "";
    const computedSticksDescription = description || "";

    const mediaGallery = currentProductData.current.product?.mediaGallery || [];
    const computedProductGalleryItems = mediaGallery.map((item) => ({
      ...item,
      path: item.url,
    }));

    const filteredGalleryAssets = getFilteredImagesByRole(computedProductGalleryItems, "dynamic_picture_bundle");

    return {
      sticksName: computedSticksName,
      sticksDescription: computedSticksDescription,
      productGalleryItems: filteredGalleryAssets,
      flavor,
      categories,
      color,
    };
  }, [currentProductData?.current.product]);

  const { isProductDataLoading, isInStock, stockStatus, discount, priceInformation, subtitle, productDisclaimer } =
    useProductData({
      sku,
      pageSize: 1,
      currentVariantSku: sku,
    });

  const productDetails: UseSyncProductWithCartProductData = {
    sku,
    priceInformation,
    hidePrice,
    isCommerceDisabled,
  };

  const { quantityLimit, isQuantityLimitReached, shouldRenderPrice, isCartLoaded } =
    useSyncProductWithCart(productDetails);

  useEffect(() => {
    if (isProductDataLoading) {
      return;
    }

    dispatchViewItemEvent({
      productName: sticksName,
      sku,
      color,
      flavor,
      categories,
      discount,
      priceInformation,
    });
  }, [isProductDataLoading, flavor]);

  useEffect(() => {
    messageBus.publish("update.breadcrumb.name", {
      payload: {
        breadcrumbName: sticksName,
      },
    });
    window.document.title = sticksName;
  }, [messageBus, sticksName]);

  return (
    <div className="productDetails__container" data-testid="sticks-product-details">
      <div className="carousel__wrapper carousel__bottomSpacing productDetails__carousel">
        <ProductCarousel
          items={productGalleryItems}
          seekLabel={__("productdetails.carousel.video.seek")}
          volumeLabel={__("productdetails.carousel.video.volume")}
          playLabel={__("productdetails.carousel.video.play")}
          replayLabel={__("productdetails.carousel.video.replay")}
          muteLabel={__("productdetails.carousel.video.mute")}
          soundOnLabel={__("productdetails.carousel.video.unmute")}
          showFullscreenLabel={__("productdetails.carousel.video.fullscreen")}
          previousSlideLabel={__("productdetails.carousel.previous_slide")}
          nextSlideLabel={__("productdetails.carousel.next_slide")}
        />
      </div>

      <div className="productDetails__productInfo">
        <ProductHeading
          description={subtitle}
          productName={sticksName}
          badgeText={overwriteBadgeText ?? badgeText}
          hasBadge={overwriteBadgeText ? true : hasBadge}
        />

        {shouldRenderPrice && (
          <ProductPrice
            hideDiscountBadge={!hasDiscountBadge}
            hideStrikethroughPrice={!hasStrikethroughPrice}
            priceInformation={priceInformation}
            discount={discount}
            isFetching={isProductDataLoading}
            showMonthlyBadge={isSubscriptionSelected}
          />
        )}

        {!!productDisclaimer && (
          <Typography variant={"body1"} className="product-details__disclaimer">
            {productDisclaimer}
          </Typography>
        )}

        {isQuantityLimitReached && (
          <ProductQuantityError className="product-details__quantity-error" isVisible quantityLimit={quantityLimit} />
        )}

        <div className="product-details__sticks-form-wrapper">
          <SticksForm
            isSubscriptionsEnabled={isSubscriptionsEnabled}
            isOutOfStockProductSelected={!isInStock}
            isCartLoaded={isCartLoaded}
            setIsSubscriptionSelected={setIsSubscriptionSelected}
            isQuantityLimitReached={isQuantityLimitReached}
          />
        </div>

        <div className={"product-information-block"}>
          <StockStatus stockStatus={stockStatus} isProductDataLoading={isProductDataLoading} />
          <ProductDescription description={sticksDescription} />
        </div>
      </div>
    </div>
  );
};
